import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import {
  LatestPost,
  LatestPostsComponent,
} from '../../../components/sections/latest-posts/latest-posts.component';
import {
  LatestAlert,
  LatestAlertsComponent,
} from '../../../components/sections/latest-alerts/latest-alerts.component';

@Component({
  selector: 'app-bulk-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    LatestPostsComponent,
    LatestAlertsComponent,
  ],
  templateUrl: './latest-posts-sidebar.component.html',
  styleUrl: './latest-posts-sidebar.component.scss',
})
export class LatestPostsSidebarComponent implements OnInit, OnDestroy {
  @Input() maxSidebarCards = 3;
  @Input() naPlaceholder = './assets/images/na.png';

  posts: LatestPost[] = [];
  alerts: LatestAlert[] = [];

  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';

  private destroy$ = new Subject<void>();

  private readonly onStorage = (event: StorageEvent) => {
    if (
      event.key === this.LATEST_POSTS_KEY ||
      event.key === this.LATEST_ALERTS_KEY
    ) {
      this.loadFromStorage();
    }
  };

  constructor(
    private router: Router,
    private coreService: CoreService,
  ) {}

  ngOnInit(): void {
    this.loadFromStorage();

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.onStorage);
    }

    this.coreService.latestPosts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(posts => {
        if (posts && posts.length > 0) {
          this.posts = posts.slice(0, this.maxSidebarCards);
        }
      });

    this.coreService.latestAlerts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(alerts => {
        if (alerts && alerts.length > 0) {
          this.alerts = alerts.slice(0, this.maxSidebarCards);
        }
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.onStorage);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFromStorage(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      this.alerts = [];
      return;
    }

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();
  }

  private loadSidebarPosts(): void {
    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as LatestPost[] | null;

      this.posts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (error) {
      console.error('Failed to parse latest posts', error);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);

    if (!raw) {
      this.alerts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as LatestAlert[] | null;

      this.alerts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (error) {
      console.error('Failed to parse latest alerts', error);
      this.alerts = [];
    }
  }

  viewRow(row: LatestPost): void {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;

    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  viewAlert(alert: LatestAlert): void {
    this.router.navigate([
      '/preview-post',
      alert.format,
      alert.posting_queue_id,
    ]);
  }
}